import { call, put, select, takeLatest } from 'redux-saga/effects'

import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'

import { fetchRequest, fetchSuccess, fetchFailure, exportRequest, exportSuccess, exportFailure } from './feedbackSlice'

export const feedbackActions = {
  FETCH_LIST: 'DEVTOOLS_NEW/FEEDBACK/FETCH_LIST',
  EXPORT_CSV: 'DEVTOOLS_NEW/FEEDBACK/EXPORT_CSV',
}

const buildFilterParams = filters => {
  const params = {
    search: filters.search || undefined,
    start: filters.start || undefined,
    end: filters.end || undefined,
    feedbackType: filters.feedbackType !== 'all' ? filters.feedbackType : undefined,
    version: filters.version,
  }
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined))
}

const extractFilenameFromContentDisposition = contentDisposition => {
  const match = /filename="?([^"]+)"?/.exec(contentDisposition || '')
  return match?.[1] || 'agent_feedbacks.csv'
}

function* fetchFeedbackListSaga({ t, payload }) {
  yield put(fetchRequest())

  try {
    const { extraRoutes } = yield select(selectAuthData)
    const url = extraRoutes?.devTools?.feedback?.list
    if (!url) {
      const message = t?.('feedback.errors.missing_list_url') || 'Missing feedback list url'
      yield put(fetchFailure(message))
      return
    }

    const result = yield call(callApi, {
      endpoint: url,
      method: 'GET',
      showErrorSnackbar: false,
      queryParams: {
        ...buildFilterParams(payload || {}),
        page: payload?.page || 1,
        perPage: payload?.perPage || 20,
      },
    })

    yield put(
      fetchSuccess({
        items: result?.data || [],
        total: result?.total || 0,
        perPage: result?.per_page,
        page: result?.current_page,
      }),
    )
  } catch (error) {
    const message = error?.responseData?.error || error?.message || 'Error fetching feedback list'
    yield put(fetchFailure(message))
  }
}

function* exportFeedbackCsvSaga({ enqueue, t, payload }) {
  yield put(exportRequest())

  try {
    const { extraRoutes, authTokenSession, country } = yield select(selectAuthData)
    const template = extraRoutes?.devTools?.feedback?.export
    if (!template) {
      const message = t?.('feedback.errors.missing_export_url') || 'Missing feedback export url'
      yield put(exportFailure(message))
      if (enqueue) yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message })
      return
    }

    const query = new URLSearchParams({ ...buildFilterParams(payload || {}), lang: country })
    const url = `${template}?${query.toString()}`
    const config = {
      method: 'GET',
      mode: 'cors',
      headers: {
        Accept: 'text/csv,application/json',
        Authorization: 'Bearer ' + authTokenSession,
      },
    }

    const response = yield call(fetch, url, config)
    const contentType = response.headers.get('content-type') || ''

    if (!contentType.includes('text/csv')) {
      const data = yield call([response, response.json])
      const message = data?.message || data?.error_text || t?.('feedback.errors.export_failed') || 'Export failed'
      yield put(exportFailure(message))
      if (enqueue) yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message })
      return
    }

    const blob = yield call([response, response.blob])
    const filename = extractFilenameFromContentDisposition(response.headers.get('content-disposition'))
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)

    yield put(exportSuccess())
  } catch (error) {
    const message = error?.message || 'Error exporting feedback'
    yield put(exportFailure(message))
    if (enqueue) {
      yield call(showSnackbar, enqueue, { type: SNACKBAR_TYPES.ERROR, message })
    }
  }
}

export default function* feedbackSagas() {
  yield takeLatest(feedbackActions.FETCH_LIST, fetchFeedbackListSaga)
  yield takeLatest(feedbackActions.EXPORT_CSV, exportFeedbackCsvSaga)
}
